<template>
  <div class="plan-amount-and-date-page">
    <div class="container">
      <div class="headline">{{ currentPlan.name }}</div>
      <amount-input class="amount-input" v-model="targetAmount" label="כמה נראה לך שצריך?"/>
      <div class="amount-note">לא חייבים את הסכום המדוייק כרגע, תמיד אפשר לחזור ולערוך</div>
      <date-picker @select="setDeadline"/>
      <div class="monthly-amount-note">כלומר, צריך כל חודש לשים בצד:</div>
      <div class="monthly-amount-container">
        <div class="monthly-amount-box">
          <lottie-player v-if="loading" :src="JSON.stringify(require('@/assets/animations/loading-dots.json'))" :autoplay="true"/>
          <div v-else-if="!loading && calculatedMonthlyAmount" class="amount">{{ calculatedMonthlyFormattedAmount }} ₪</div>
        </div>
        <plan-status-note v-if="!loading && calculatedMonthlyAmount" class="status-note"
                          :text="noteText" :is-on-track="isCurrentPlanOnTrack" is-bold />
      </div>
      <riseup-button class="button" :disabled="!targetAmount || !deadline" title="שמירה" size="slim" variant="neutral" :action="onDone" />
    </div>
  </div>
</template>

<script>
import BaseUi from 'base-ui';
import { mapActions, mapState, mapGetters } from 'vuex';
import moneyUtils from '@/utils/money';
import _ from 'lodash';
import PlanStatusNote from '@/pages/responsive-pages/authenticated/navigation-pages/plan-ahead/PlanStatusNote';
import Segment from '@/Segment';
import DatePicker from '../DatePicker';

export default {
  name: 'PlanAmountAndDatePage',
  components: {
    PlanStatusNote,
    DatePicker,
    AmountInput: BaseUi.AmountInput,
    RiseupButton: BaseUi.RiseupButton,
  },
  data() {
    return {
      targetAmount: null,
      deadline: null,
      loading: false,
    };
  },
  props: {
    plan: {
      type: Object,
      require: false,
    },
  },
  created() {
    if (this.currentPlan.targetAmount && this.currentPlan.deadline) {
      this.targetAmount = this.currentPlan.targetAmount;
      this.deadline = this.currentPlan.deadline;
    }
  },
  computed: {
    ...mapState('planAhead', ['calculatedMonthlyAmount', 'currentPlan']),
    ...mapGetters('planAhead', ['totalPlansMonthlyAmount']),
    ...mapGetters('cashflowView', ['riseupGoal']),
    calculatedMonthlyFormattedAmount() {
      return moneyUtils.formatAmountWithFraction(this.calculatedMonthlyAmount, 0);
    },
    totalRemaining() {
      const remaining = this.riseupGoal - this.totalPlansMonthlyAmount + this.existingPlanMonthlyTarget;
      return moneyUtils.formatAmountWithFraction(remaining, 0);
    },
    existingPlanMonthlyTarget() {
      return this.plan ? this.plan.monthlyTarget : 0;
    },
    isCurrentPlanOnTrack() {
      return this.totalPlansMonthlyAmount + this.calculatedMonthlyAmount - this.existingPlanMonthlyTarget <= this.riseupGoal;
    },
    noteText() {
      return this.isCurrentPlanOnTrack
        ? `לפי התזרים שלך החודש, נראה שאפשר לשים עד ${this.totalRemaining} ₪  לתוכניות שלך`
        : 'כרגע נראה שלא ישאר מספיק בצד החודש כדי לעמוד בתוכנית הזאת. בינתיים אפשר להוסיף לרשימה, ולערוך בהמשך.';
    },
  },
  methods: {
    ...mapActions('planAhead', ['getCalculatedMonthlyAmount']),
    async setTargetAmount(value) {
      this.targetAmount = value;
      await this.handleMonthlyCalculation();
    },
    async setDeadline(value) {
      this.deadline = value;
      await this.handleMonthlyCalculation();
    },
    async handleMonthlyCalculation() {
      if (this.deadline && this.targetAmount) {
        this.loading = true;
        await this.setCalculatedMonthlyAmount();
      }
    },
    async setCalculatedMonthlyAmount() {
      const targetAmount = this.plan ? this.targetAmount - this.plan.currentAmount : this.targetAmount;
      const data = { deadline: this.deadline, targetAmount };
      await this.getCalculatedMonthlyAmount(data);
      this.loading = false;
      Segment.trackUserInteraction('PlanAhead_Flow_AmountPage_OnCalculationChanged', { isAble: this.isCurrentPlanOnTrack });
    },
    onDone() {
      Segment.trackUserInteraction('PlanAhead_Flow_AmountPage_NextClicked', { isAble: this.isCurrentPlanOnTrack });
      this.$emit('done', { targetAmount: this.targetAmount, deadline: this.deadline });
    },
  },
  watch: {
    targetAmount: _.debounce(async function debounceMonthlyCalc(newVal) {
      if (newVal) {
        await this.handleMonthlyCalculation();
      }
    }, 500),
  },
};
</script>

<style scoped lang="scss">
@import '~base-ui/src/scss/riseup-colors';

.plan-amount-and-date-page {
  overflow: auto;
  height: 100%;

  .container {
    margin: 0 24px;

    .headline {
      height: 75px;
      display: flex;
      align-items: center;
      font-weight: bold;
      font-size: 16px;
    }

    .amount-input {
      margin-bottom: 8px;
    }

    .amount-note {
      font-size: 14px;
      margin-bottom: 56px;
    }

    .monthly-amount-note {
      display: flex;
      align-items: center;
      margin-top: 62px;
      font-weight: bold;
      font-size: 20px;
      margin-bottom: 17px;
    }

    .monthly-amount-container {
      height: 160px;

      .monthly-amount-box {
        height: 63px;
        background: #F1F1F1;
        opacity: 0.8;
        border-radius: 5px;
      }

      .status-note {
        padding-top: 8px;
      }
    }

    .amount {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
      font-size: 44px;
      color: $riseup_gray_2;
    }

    .button {
      margin-bottom: 20px;
    }
  }
}

</style>
